/* eslint-disable @typescript-eslint/no-explicit-any */
import { BatchOperationAdd, BatchOperationUpdate, ConcurrencyError, IRListItem } from '@rhybus/api'
import React, { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { INQUIRIES } from '../../Configuration'
import { sitePath } from '../../assets/ts/api/api'
import { mpfRListsHttp } from '../../assets/ts/api/mpf-r-lists-http'
import { transactionBatch } from '../../assets/ts/data/data'
import { LIST_NAME as DISCUSSION_LIST_NAME } from '../../assets/ts/data/discussions'
import { LIST_NAME as DUEOUT_LIST_NAME } from '../../assets/ts/data/dueout/dueout'
import { LIST_NAME as DUEOUT_DISCUSSION_LIST } from '../../assets/ts/data/dueout/dueout-discussions'
import { LIST_NAME as DUEOUT_HISTORY_LIST } from '../../assets/ts/data/dueout/dueout-history'
import { STATUS_CODE } from '../../assets/ts/data/dueout/dueout-status-metadata'
import { Inquiry, LIST_NAME, PersonDetails } from '../../assets/ts/data/inquiry/inquiry'
import { LIST_NAME as HISTORY_LIST_NAME } from '../../assets/ts/data/inquiry/inquiry-history'
import { determineStatusCode } from '../../assets/ts/formatting/formatting'
import {
  encodeHTML,
  formatBody,
  handleRoute,
  sendEmail,
  sendNotification,
  updateInvolvedUsers,
} from '../../assets/ts/general/general'
import { SURVEY_REQUIRED_LIST } from '../../pages/surveyreport/customerSurvey'
import { MetadataContext } from '../../store/metadata-context-provider'
import { UserContext } from '../../store/user-context-provider'
import {
  ACTION_BUTTON_POPUP_CONTROL,
  ACTIONS,
  COMMENT_TYPES,
  CONCURRENCY_ERR_MSG,
  DISCUSSION_LOG,
  DUEOUTS_ACTIONS,
  INQUIRY_ACTION_STRINGS,
} from '../../utils/constants/global-constants'
import { SurveyRequiredCols } from '../../utils/constants/global-list-columns'
import InputTextArea from '../../utils/controls/input-text-area'
import DialogBox from '../alert/dialog-box'
import ActionButtons from './action-buttons'
import Buttons from './buttons'
import './buttons.css'

interface Props {
  action: string
  actionType: string
  buttonColor: string
  currentUserRole
  data
  icon: string
  id?: any
  name: string
  popupBtnRef: React.RefObject<HTMLInputElement>
  popupClassName: string
  popupCloseOpenFunctionality: (
    type: string,
    value: number,
    top: number,
    xPosition: number,
    left: number,
    elementHeight: number,
    offsetWidth: number
  ) => void
  setRefresh: (refresh: boolean) => void
  showHide: boolean
  statusRelativeToUser: string
  style: React.CSSProperties
}

const BasicActionButton = (props: Props) => {
  const {
    action,
    buttonColor,
    currentUserRole,
    data,
    icon,
    id,
    name,
    popupBtnRef,
    popupClassName,
    popupCloseOpenFunctionality,
    setRefresh,
    showHide,
    statusRelativeToUser,
    style,
  } = props

  const navigate = useNavigate()
  const { me } = useContext(UserContext)
  const { tooltipObj } = useContext(MetadataContext)
  const [userInputs, setUserInputs] = useState({
    comment: '',
  })
  const [showErrorMsgs, setShowErrorMsgs] = useState({
    comment: { show: false },
  })
  const [showConfirmReload, setShowConfirmReload] = useState<boolean>(false)
  const [tooltip, setTooltip] = useState('')

  const onChangeInputSelect = (e: any) => {
    setUserInputs({ ...userInputs, comment: e.target.value })
    setShowErrorMsgs({ ...showErrorMsgs, comment: { show: false } })
  }

  const validateInputs = () => {
    let isValid = true

    if (userInputs.comment === '' || userInputs.comment === null) {
      showErrorMsgs.comment.show = true
      isValid = false
    }

    setShowErrorMsgs({ ...showErrorMsgs })
    return isValid
  }
  const determineCommentType = (action): string => {
    if ([action].includes([INQUIRY_ACTION_STRINGS.CancelResponseRequired])) {
      return COMMENT_TYPES.Internal
    } else {
      return COMMENT_TYPES.Customer
    }
  }
  const handleSubmit = async () => {
    const isValid = validateInputs()

    if (isValid) {
      const currUser = currentUserRole?.replace('[SECTION_NAME]', data['Section/MetadataValue'])

      const itemTime = new Date()
      const userRoleForDiscussion = DISCUSSION_LOG[action]
      const dataObj: any = {
        ItemModified: itemTime,
        ItemModifiedBy: me.userId,
        eKey: data.eKey,
        eTag: data.eTag,
      }

      const historyObj = {
        InquiryGUID: data.InquiryGUID,
        InquiryID: data.InquiryID,
        ItemCreated: itemTime,
        ItemCreatedBy: me.userId,
        ItemModified: itemTime,
        ItemModifiedBy: me.userId,
        SectionCode: data.SectionCode,
        ServicingBy: data.CurrentlyServicingBy,
        StatusCode: determineStatusCode(data.StatusCode, data.CurrentlyServicingBy),
        UserRole: currUser,
      }

      const discussionObj = {
        Comment: encodeHTML(userInputs.comment.trim()),
        CommentType: determineCommentType(action),
        InquiryGUID: data.InquiryGUID,
        InquiryID: data.InquiryID,
        IsActionComment: true,
        ItemCreated: itemTime,
        ItemCreatedBy: me.userId,
        ItemModified: itemTime,
        ItemModifiedBy: me.userId,
        Subject: userRoleForDiscussion,
        UserRole: currUser,
      }
      await createListObjects(
        action,
        dataObj || {},
        itemTime,
        historyObj || {},
        discussionObj || {},
        userRoleForDiscussion
      )

      if (data.InquiryID) {
        const pageAfterResponse = data.ResponseRequired && action === 'Respond' &&
        (currUser.includes('Technician') || currUser.includes('Supervisor') || currUser.includes('Section Chief'))
        try {
          const batchResult: any = await transactionBatch([
            { data: dataObj, list: LIST_NAME, op: BatchOperationUpdate },
            {
              data: historyObj,
              list: HISTORY_LIST_NAME,
              op: BatchOperationAdd,
            },
            {
              data: discussionObj,
              list: DISCUSSION_LIST_NAME,
              op: BatchOperationAdd,
            },
          ])
          if (batchResult.result === 'PASS') {
            await handleEmails(me)
            await sendNotification(historyObj.ActionCode, data, me, currUser, false)

            if (dataObj?.StatusCode === ACTIONS.Closed) {
              await filterSurveyRequiredDataObj(data)
            }
            pageAfterResponse? navigate(`${INQUIRIES}`) : setRefresh(true)
          }
        } catch (error) {
          if (error instanceof ConcurrencyError) {
            setShowConfirmReload(true)
          } else {
            console.error(error)
          }
        }
      } else {
        try {
          const dueoutList = await mpfRListsHttp().getList(DUEOUT_LIST_NAME)
          const dueouthistoryList = await mpfRListsHttp().getList(DUEOUT_HISTORY_LIST)
          const dueoutdiscussionList = await mpfRListsHttp().getList(DUEOUT_DISCUSSION_LIST)
          const successful = await dueoutList.addOrUpdateItem(dataObj as IRListItem)
          if (successful !== undefined) {
            await dueouthistoryList.addOrUpdateItem(historyObj as IRListItem)
            await handleEmails(me)
            await sendNotification(historyObj.ActionCode, data, me, currUser, true)
            await dueoutdiscussionList.addOrUpdateItem(discussionObj as IRListItem)
            setRefresh(true)
          }
        } catch (error) {
          console.error(error)
        }
      }
    }
  }

  const getStatusCode = (data, isElevated) => {
    if (isElevated) {
      if (
        data?.respondToUser?.PreviousStatusCode.includes('TECHNICIAN') ||
        data?.respondToUser?.PreviousStatusCode.includes('SUPERVISOR') ||
        data?.respondToUser?.PreviousStatusCode.includes('SECTION_CHIEF')
      ) {
        return data?.respondToUser?.PreviousStatusCode
      } else {
        return `${statusRelativeToUser.split('-')[0]}-${'TECHNICIAN'}`
      }
    } else if (data.PreviousStatusCode) {
      if (
        data?.respondToUser?.PreviousStatusCode.includes('TECHNICIAN') ||
        data?.respondToUser?.PreviousStatusCode.includes('SUPERVISOR') ||
        data?.respondToUser?.PreviousStatusCode.includes('SECTION_CHIEF')
      ) {
        return data?.respondToUser?.PreviousStatusCode
      } else {
        return `${data['PreviousSection/MetadataValue']
          .toUpperCase()
          .split(' ')
          .join('_')}-${'TECHNICIAN'}`
      }
    } else {
      return data.StatusCode
    }
  }

  useEffect(() => {
    const isInquiry = props?.actionType === 'Inquiry'
    switch(action) {
      case 'CancelInquiry':
        setTooltip(tooltipObj?.cancelInquiryComment?.TooltipDescription)
        break
      case 'CancelDueout':
        setTooltip(tooltipObj?.cancelDueOutComment?.TooltipDescription)
        break
        case 'Respond':
          setTooltip(isInquiry ?
            tooltipObj?.respondComment?.TooltipDescription : 
            tooltipObj?.respondDueOutComment?.TooltipDescription
          )
          break
        case 'Reopen':
            setTooltip(isInquiry ?
            tooltipObj?.reopenComment?.TooltipDescription : 
            tooltipObj?.reopenDueOutComment?.TooltipDescription
          )
            break
        case 'Complete':
          setTooltip(tooltipObj?.completeDueOutComment?.TooltipDescription)
          break
        case 'Resolve':
          setTooltip(tooltipObj?.resolveComment?.TooltipDescription)
          break
        case 'Close':
          setTooltip(tooltipObj?.closeComment?.TooltipDescription)
          break
        case 'CloseDueout':
          setTooltip(tooltipObj?.closeDueOutComment?.TooltipDescription)
          break
        case 'CustomerAction':
          setTooltip(tooltipObj?.customerActionRequiredComment?.TooltipDescription)
          break
        case 'PendingActionOutside':
          setTooltip(tooltipObj?.pendingActionOutsideComment?.TooltipDescription)
          break
        case 'OutsideActionComplete':
          setTooltip(tooltipObj?.actionOutsideCompleteComment?.TooltipDescription)
          break
        case 'CustomerActionComplete':
          setTooltip(tooltipObj?.customerActionCompleteComment?.TooltipDescription)
          break
        case 'CancelResponseRequired':
          setTooltip(tooltipObj?.cancelResponceRequiredComment?.TooltipDescription)
    }
  }, [tooltipObj, action])

  const createListObjects = async (
    title,
    dataObj,
    itemTime,
    historyObj,
    discussionObj,
    subject
  ) => {
    const currUser = currentUserRole?.replace('[SECTION_NAME]', data['Section/MetadataValue'])
    switch (title) {
      // Action Outside Portal Complete
      case ACTION_BUTTON_POPUP_CONTROL.OutsideActionComplete:
        dataObj.AssignedTo = me.userId
        dataObj.InvolvedUsers = updateInvolvedUsers(data, me.userId)
        dataObj.PendingActionOutsideDate = null
        dataObj.PendingActionOutside = false
        dataObj.StatusCode = determineStatusCode(statusRelativeToUser, data.CurrentlyServicingBy)

        historyObj.ActionCode = ACTIONS.OutsideActionComplete
        historyObj.StatusCode = determineStatusCode(statusRelativeToUser, data.CurrentlyServicingBy)
        historyObj.PreviousStatusCode = data.PreviousStatusCode
        historyObj.ServicingBy = data.CurrentlyServicingBy
        break
      // Cancel Inquiry
      case ACTION_BUTTON_POPUP_CONTROL.CancelInquiry:
        dataObj.PreviousStatusCode = data.StatusCode
        dataObj.CanceledDate = itemTime
        dataObj.StatusCode = ACTIONS.Canceled

        historyObj.StatusCode = ACTIONS.Canceled
        historyObj.PreviousStatusCode = data.StatusCode
        historyObj.ActionCode = 'CANCEL'
        break
      // Customer Action Complete
      case ACTION_BUTTON_POPUP_CONTROL.CustomerActionComplete:
        dataObj.CustomerActionRequired = false
        dataObj.CustomerActionRequiredDate = null
        
        historyObj.ActionCode = ACTIONS.CustomerActionComplete
        historyObj.ServicingBy = data.CurrentlyServicingBy
        historyObj.PreviousStatusCode = data.PreviousStatusCode
        if (currentUserRole !== 'Customer') {

          dataObj.AssignedTo = me.userId
          dataObj.InvolvedUsers = updateInvolvedUsers(data, me.userId)
          dataObj.StatusCode = determineStatusCode(
            statusRelativeToUser,
            data.CurrentlyServicingBy
          )

          historyObj.ActionCode = ACTIONS.CustomerActionComplete
          historyObj.StatusCode = determineStatusCode(
            statusRelativeToUser,
            data.CurrentlyServicingBy
          )
          historyObj.PreviousStatusCode = data.PreviousStatusCode
          historyObj.ServicingBy = data.CurrentlyServicingBy
          historyObj.AssignedTo = me.userId
        } else {
          dataObj.CustomerActionRequired = false
          dataObj.CustomerActionRequiredDate = null

          historyObj.AssignedTo = data.AssignedTo
          historyObj.ServicingBy = data.CurrentlyServicingBy
          historyObj.PreviousStatusCode = data.PreviousStatusCode
          historyObj.StatusCode = determineStatusCode(data.StatusCode, data.CurrentlyServicingBy)
        }
        break
      // // Customer Action Required
      case ACTION_BUTTON_POPUP_CONTROL.CustomerAction:
        dataObj.CustomerActionRequired = true
        dataObj.CustomerActionRequiredDate = itemTime
        dataObj.StatusCode = determineStatusCode(statusRelativeToUser, data.CurrentlyServicingBy)
        dataObj.InvolvedUsers = updateInvolvedUsers(data, me.userId)
        dataObj.AssignedTo = me.userId

        historyObj.ActionCode = ACTIONS.CustomerActionRequired
        historyObj.AssignedTo = null
        historyObj.StatusCode = determineStatusCode(statusRelativeToUser, data.CurrentlyServicingBy)
        historyObj.PreviousStatusCode = data.PreviousStatusCode
        historyObj.ServicingBy = data.CurrentlyServicingBy
        historyObj.AssignedTo = me.userId

        break
      // Pending Action Outside Portal
      case ACTION_BUTTON_POPUP_CONTROL.PendingActionOutside:
        dataObj.PendingActionOutsideDate = itemTime
        dataObj.PendingActionOutside = true
        dataObj.StatusCode = determineStatusCode(statusRelativeToUser, data.CurrentlyServicingBy)
        dataObj.InvolvedUsers = updateInvolvedUsers(data, me.userId)

        historyObj.ActionCode = ACTIONS.PendingActionOutsideCP
        historyObj.StatusCode = determineStatusCode(statusRelativeToUser, data.CurrentlyServicingBy)
        historyObj.PreviousStatusCode = data.PreviousStatusCode
        historyObj.ServicingBy = data.CurrentlyServicingBy
        historyObj.AssignedTo = me.userId
        dataObj.AssignedTo = me.userId

        break
      // Resolved
      case ACTION_BUTTON_POPUP_CONTROL.Resolve:
        dataObj.AssignedTo = me.userId
        dataObj.StatusCode = ACTIONS.Resolved
        dataObj.PreviousStatusCode = data.StatusCode
        dataObj.ResolvedDate = itemTime
        dataObj.InvolvedUsers = updateInvolvedUsers(data, me.userId)

        historyObj.AssignedTo = me.userId
        historyObj.StatusCode = ACTIONS.Resolved
        historyObj.PreviousStatusCode = data.StatusCode
        historyObj.ActionCode = 'RESOLVE'
        historyObj.ServicingBy = data.CurrentlyServicingBy
        discussionObj.Subject = `${DISCUSSION_LOG.Resolve} ${currUser}`
        break
      // Closed
      case ACTION_BUTTON_POPUP_CONTROL.Close:
        dataObj.StatusCode = ACTIONS.Closed
        dataObj.PreviousStatusCode = data.StatusCode
        dataObj.ClosedDate = itemTime

        historyObj.StatusCode = ACTIONS.Closed
        historyObj.PreviousStatusCode = ACTIONS.Closed
        historyObj.ActionCode = ACTIONS.Closed
        break
      // Reopened
      case ACTION_BUTTON_POPUP_CONTROL.Reopen:
        dataObj.StatusCode = ACTIONS.Reopened
        dataObj.PreviousStatusCode = data.StatusCode
        dataObj.ReopenedDate = itemTime
        dataObj.AssignedTo = null
        dataObj.InvolvedUsers = []

        historyObj.StatusCode = ACTIONS.Reopened
        historyObj.PreviousStatusCode = data.StatusCode
        historyObj.AssignedTo = null
        historyObj.ActionCode = 'REOPEN'
        break
      // Cancel Response Requested
      case ACTION_BUTTON_POPUP_CONTROL.CancelResponseRequired:
        dataObj.StatusCode = data.PreviousStatusCode
        dataObj.PreviousStatusCode = data.StatusCode
        dataObj.SectionCode = data.PreviousSectionCode
        dataObj.PreviousSectionCode = data.SectionCode
        dataObj.CategoryCode = data.PreviousCategoryCode
        dataObj.PreviousCategoryCode = data.CategoryCode
        dataObj.SubCategoryCode = data.PreviousSubCategoryCode
        dataObj.PreviousSubCategoryCode = data.SubCategoryCode
        dataObj.ResponseRequired = false
        dataObj.InvolvedUsers = updateInvolvedUsers(data, me.userId)

        historyObj.ActionCode = ACTIONS.CancelResponseRequired
        historyObj.SectionCode = data.SectionCode
        historyObj.PreviousSectionCode = data.PreviousSectionCode
        historyObj.StatusCode = data.PreviousStatusCode
        historyObj.PreviousStatusCode = data.StatusCode
        historyObj.AssignedTo = null
        discussionObj.Subject = `${DISCUSSION_LOG.CancelResponseRequired} by ${currentUserRole?.replace('[SECTION_NAME]', data['PreviousSection/MetadataValue'])}`
        break
      // Respond to Elevated and Response Requested from other section.
      case ACTION_BUTTON_POPUP_CONTROL.Respond:
        dataObj.InvolvedUsers = updateInvolvedUsers(data, me.userId)
        dataObj.AssignedTo = null
        if (data.ResponseRequired) dataObj.ResponseRequired = false

        if (data?.InquiryID) {
          const isElevated = data?.respondToUser?.ActionCode == ACTIONS.Elevated
          const isAssignedToOtherSection =
            data?.respondToUser?.ActionCode == ACTIONS.ChangedSection ||
            data?.respondToUser?.ActionCode == ACTIONS.OtherSection

          dataObj.StatusCode = getStatusCode(data, isElevated)
          dataObj.PreviousStatusCode = data.StatusCode

          if (isAssignedToOtherSection) {
            dataObj.SectionCode = data.PreviousSectionCode
            dataObj.PreviousSectionCode = data.SectionCode

            dataObj.CategoryCode = data.PreviousCategoryCode
            dataObj.PreviousCategoryCode = data.CategoryCode
            dataObj.SubCategoryCode = data.PreviousSubCategoryCode
            dataObj.PreviousSubCategoryCode = data.SubCategoryCode

            if (data.ResponseRequired) dataObj.ResponseRequired = false

            historyObj.SectionCode = data.PreviousSectionCode
            historyObj.PreviousSectionCode = dataObj.PreviousSectionCode
          }
          if (
            isElevated &&
            data?.respondToUser?.ServicingBy === 'MPF' &&
            data?.respondToUser?.PreviousServicingBy === 'CSS'
          ) {
            dataObj.StatusCode = `${statusRelativeToUser.split('-')[0]}-${'CSS'}`
            dataObj.CurrentlyServicingBy = 'CSS'
            
          }
          dataObj.AssignedTo = data.respondToUser.ItemCreatedBy.userID
          historyObj.AssignedTo = data.respondToUser.ItemCreatedBy.userID
          historyObj.ActionCode = ACTIONS.Responded
          historyObj.ServicingBy = data.CurrentlyServicingBy
          historyObj.PreviousServicingBy = 'MPF'
          historyObj.StatusCode = dataObj.StatusCode
          historyObj.PreviousStatusCode = data.StatusCode
        } else {
          dataObj.StatusCode = STATUS_CODE.IN_PROGRESS

          historyObj.DueOutGUID = discussionObj.DueOutGUID = data.DueOutGUID
          historyObj.DueOutID = discussionObj.DueOutID = data.DueOutID

          historyObj.ActionCode = DUEOUTS_ACTIONS.RESPOND
          historyObj.ServicingBy = data.ServicingBy

          delete historyObj.InquiryGUID
          delete historyObj.InquiryID
          delete discussionObj.InquiryGUID
          delete discussionObj.InquiryID
        }
        discussionObj.Subject = `${currUser} ${subject}`
        if (data.DueOutID){
          discussionObj.Subject = `${data['Section/MetadataValue']}  ${currUser}  ${subject}`
        }
        break
      case ACTION_BUTTON_POPUP_CONTROL.Complete:
        dataObj.StatusCode = STATUS_CODE.COMPLETED
        historyObj.DueOutGUID = discussionObj.DueOutGUID = data.DueOutGUID
        historyObj.DueOutID = discussionObj.DueOutID = data.DueOutID
        historyObj.ServicingBy = data.ServicingBy
        historyObj.ActionCode = DUEOUTS_ACTIONS.COMPLETE
        discussionObj.Subject = `${currUser}  ${subject}`
        if (data.DueOutID && !(data.Customer.userID === me.userId)){
          discussionObj.Subject = `${data['Section/MetadataValue']}  ${currUser}  ${subject}`
        }
        delete historyObj.InquiryGUID
        delete historyObj.InquiryID
        delete discussionObj.InquiryGUID
        delete discussionObj.InquiryID
        break
      case ACTION_BUTTON_POPUP_CONTROL.CloseDueout:
        dataObj.StatusCode = STATUS_CODE.CLOSED
        dataObj.ClosedDate = itemTime
        historyObj.DueOutGUID = discussionObj.DueOutGUID = data.DueOutGUID
        historyObj.DueOutID = discussionObj.DueOutID = data.DueOutID
        historyObj.ServicingBy = data.ServicingBy
        historyObj.ActionCode = DUEOUTS_ACTIONS.CLOSE
        discussionObj.Subject = `${currUser}  ${subject}`
        if (data.DueOutID){
          discussionObj.Subject = `${data['Section/MetadataValue']}  ${currUser}  ${subject}`
        }
        delete historyObj.InquiryGUID
        delete historyObj.InquiryID
        delete discussionObj.InquiryGUID
        delete discussionObj.InquiryID
        break
      case ACTION_BUTTON_POPUP_CONTROL.CancelDueout:
        dataObj.StatusCode = STATUS_CODE.CANCELED
        historyObj.DueOutGUID = discussionObj.DueOutGUID = data.DueOutGUID
        historyObj.DueOutID = discussionObj.DueOutID = data.DueOutID
        historyObj.ServicingBy = data.ServicingBy
        historyObj.ActionCode = DUEOUTS_ACTIONS.CANCEL
        discussionObj.Subject = `${currUser}  ${subject}`
        if (data.DueOutID){
          discussionObj.Subject = `${data['Section/MetadataValue']}  ${currUser}  ${subject}`
        }
        delete historyObj.InquiryGUID
        delete historyObj.InquiryID
        delete discussionObj.InquiryGUID
        delete discussionObj.InquiryID
        break
    }
  }
  const resetUserInputs = () => {
    setUserInputs({
      comment: '',
    })
    setShowErrorMsgs({ ...showErrorMsgs, comment: { show: false } })

  }

  const handleEmails = async (userdetails) => {
    const userId = userdetails.userId
    const isInquiry = props?.actionType === 'Inquiry'
    const detailView = isInquiry ? 'detailview' : 'dueoutdetailview'
    const dataGUID = isInquiry ? data?.InquiryGUID : data?.DueOutGUID
    const dataID = isInquiry ? data?.InquiryID : data?.DueOutID

    const navURL = sitePath + `/${detailView}/${dataGUID}`
    let sendEmailBool = false
    let emailBody = ''

    let emailSubject = ''
    switch (action) {
      case 'Respond':
        if (!isInquiry) {
          emailSubject = `${isInquiry ? 'Inquiry' : 'Due Out'} '${dataID}' - response added`
          sendEmailBool = true
        }
        break
      case 'Complete':
        if (!isInquiry) {
          emailSubject = `Due Out '${dataID}' - has been completed`
          sendEmailBool = true
        }
        break
      case 'Resolve':
        emailSubject = `${isInquiry ? 'Inquiry' : 'Due Out'} '${dataID}' has been resolved`
        sendEmailBool = true
        break
      case 'Close':
      case 'CloseDueout':
        emailSubject = `${isInquiry ? 'Inquiry' : 'Due Out'} '${dataID}' has been closed`
        sendEmailBool = true
        break
      case 'CancelDueout':
        emailSubject = `'Due Out '${dataID}' has been canceled`
        sendEmailBool = true
        break
      case 'CustomerAction':
        emailSubject = `${isInquiry ? 'Inquiry' : 'Due Out'} '${dataID}' Customer Action Required`
        emailBody = `${isInquiry ? 'Inquiry' : 'Due Out'} '${dataID}' Customer Action Required.<br/>
        Additional information is needed to resolve your ${isInquiry ? 'inquiry' : 'due out'} '${
          data?.InquiryID
        }'.`
        sendEmailBool = true
        break
      case 'PendingActionOutside':
        emailSubject = `${
          isInquiry ? 'Inquiry' : 'Due Out'
        } '${dataID}' - Pending Action Outside CP`
        emailBody = `${isInquiry ? 'Inquiry' : 'Due Out'} '${dataID}' - Pending Action Outside CP.`
        sendEmailBool = true
        break
      case 'OutsideActionComplete':
        emailSubject = `${
          isInquiry ? 'Inquiry' : 'Due Out'
        } '${dataID}' - Action Outside CP is completed`
        emailBody = `${isInquiry ? 'Inquiry' : 'Due Out'} '${dataID}' - Action Outside CP is completed.`

        sendEmailBool = true
        break
      case 'CustomerActionComplete':
        emailSubject = `${
          isInquiry ? 'Inquiry' : 'Due Out'
        } '${dataID}' - Customer Action Completed`
        emailBody = `${
          isInquiry ? 'Inquiry' : 'Due Out'
        } '${dataID}' Customer Action Completed.<br/>
        Customer has provided the information for ${isInquiry ? 'inquiry' : 'due out'} '${
          data?.InquiryID
        }'.`
        sendEmailBool = true
        break
    }

    if (emailBody === '') emailBody = emailSubject

    const emailBodyData = {
      ...data,
      Title: emailSubject,
      bodyText: emailBody,
      isInquiry: true,
    }

    const body = formatBody(action, emailBodyData, navURL)
    const to = data.Customer.userID

    const emailObjData = {
      ...data,
      Action: action,
      ItemGUID: dataGUID,
      ItemID: dataID,
      Title: data?.Subject,
    }

    const emails: Promise<any>[] = []
    if (sendEmailBool && to && (to !== userId.toString() || action === 'Close')) {
      emails.push(
        sendEmail({
          bcc: '',
          body: body,
          bodySurvey: '',
          cc: '',
          data: emailObjData,
          isDiscussion: false,
          isSurvey: false,
          loginuserId: userId,
          navURL: navURL,
          subject: emailSubject,
          to: to,
        })
      )
    }

    if (sendEmailBool && data?.TeamMembers && data?.TeamMembers.length > 0) {
      const teamMembers = data?.TeamMembers
      for (const teamMember of teamMembers) {
        emails.push(
          sendEmail({
            bcc: '',
            body: body,
            bodySurvey: '',
            cc: '',
            data: emailObjData,
            isDiscussion: false,
            isSurvey: false,
            loginuserId: userId,
            navURL: navURL,
            subject: emailSubject,
            to: teamMember.userID,
          })
        )
      }
    }

    await Promise.all(emails)
  }
  const filterSurveyRequiredDataObj = async (inquiryData: Inquiry) => {
    try {
      const surveyRequiredList = await mpfRListsHttp().getList(SURVEY_REQUIRED_LIST)
      const surveyRequiredData: any = await surveyRequiredList.query().get()
      const filterSurveyRequiredData = surveyRequiredData?.filter(
        (item) => item.ItemGUID === inquiryData.InquiryGUID
      )

      const surveyGUID = crypto.randomUUID()

      if (!filterSurveyRequiredData.length) {
        await updateSurveyRequiredList(inquiryData, surveyGUID)
        const navURL = sitePath + `/surveyform/${surveyGUID}`

        const emailBodyData = {
          ...data,
          bodyText:
            'To better understand and improve our customer experience, we would appreciate your feedback on your recently completed inquiry.',
        }

        const bodySurvey = formatBody('survey', emailBodyData, navURL)

        const emailObjData = {
          ...data,
          Action: 'Survey Request',
          ItemGUID: data.InquiryGUID,
          ItemID: data.InquiryID,
          Title: data.Subject,
        }
        const sendEmails = {
          bcc: '',
          bodySurvey: bodySurvey,
          cc: '',
          data: emailObjData,
          isSurvey: true,
          loginuserId: me.userId,
          navURL: navURL,
          subject: `MPF Survey: Inquiry ${inquiryData.InquiryID} `,
          to: (inquiryData.Customer as PersonDetails)?.userID,
        }
        await sendEmail(sendEmails)
      }
    } catch (error) {
      console.error('Error in filterSurveyRequiredDataObj:', error)
      throw error // Throw error to propagate it up the call chain
    }
  }

  const updateSurveyRequiredList = async (inquiryData: Inquiry, surveyGUID: string) => {
    const surveyObj = {}
    surveyObj[SurveyRequiredCols.InquiryID] = inquiryData.InquiryID
    surveyObj[SurveyRequiredCols.ItemGUID] = inquiryData.InquiryGUID
    surveyObj[SurveyRequiredCols.SurveyGUID] = surveyGUID
    surveyObj[SurveyRequiredCols.IsFeedbackRequired] = true
    surveyObj[SurveyRequiredCols.CustomerEmail] = inquiryData.DutyEmail
    surveyObj[SurveyRequiredCols.CustomerDOD] = inquiryData.DoDNumber
    surveyObj[SurveyRequiredCols.MAJCOM] = inquiryData['ServicingMAJCOM/MetadataValue']
    surveyObj[SurveyRequiredCols.Section] = inquiryData['Section/MetadataValue']
    surveyObj[SurveyRequiredCols.AssignedComponent] = inquiryData['AssignedComponent/MetadataValue']
    surveyObj[SurveyRequiredCols.ServicingOrganization] = inquiryData?.ServicingOrganization
    surveyObj[SurveyRequiredCols.ItemCreatedBy] = me.userId
    surveyObj[SurveyRequiredCols.ItemCreated] = new Date()
    surveyObj[SurveyRequiredCols.ItemModified] = new Date()
    surveyObj[SurveyRequiredCols.ItemModifiedBy] = me.userId
    try {
      const surveyRequiredList = await mpfRListsHttp().getList(SURVEY_REQUIRED_LIST)
      await surveyRequiredList.addOrUpdateItem(surveyObj)
    } catch (error) {
      console.error(error, 'Error updating item')
    }
  }
  return (
    <div>
      <ActionButtons
        aria-label={name}
        className={`btn-lg font-12 ${buttonColor} whitetext text-nowrap border-radius poppins-bold text-uppercase`}
        icon={icon}
        id={id}
        isClick={action}
        label={name}
        name={name}
        popupCloseOpenFunctionality={popupCloseOpenFunctionality}
        showHide={showHide}
        type='button'
      />
      {showHide ? (
        <div
          className={`popup popup-md ${popupClassName}`}
          ref={popupBtnRef}
          style={style}
        >
          <div className='row'>
            <div className='col-md-12 mt-0'>
              <InputTextArea
                className='form-lg font-13 sourcesanspro  min-h-100 p-1'
                cols='30'
                error={showErrorMsgs.comment.show}
                formClassName=' form-vertical'
                info={tooltip}
                infoClassName='tool-tip tooltip-bottom-left font-12 sourcesanspro'
                infoIcon='icon-info color-primary'
                inputProps={{
                  className: 'font-12 poppins-medium title-color2 mb-1',
                  id: 'Comment',
                  name: 'Comment',
                  placeholder: 'Enter your Comment',
                }}
                isInfo
                isMandatory
                label='Comment'
                onChange={onChangeInputSelect}
                rows='3'
                type='textarea'
              />
            </div>
          </div>
          <div className='pt-2 d-flex gap-2'>
            <Buttons
              aria-label='Ok'
              className='btn btn-sm btn-primary whitetext font-11 border-radius ms-auto text-uppercase poppins-semibold'
              icon='icon-checked font-10 me-1'
              label='Ok'
              onClick={handleSubmit}
            />
            <ActionButtons
              className='btn-border1 title-color3 btn-sm font-11 border-radius text-uppercase poppins-semibold'
              icon='icon-close font-9 me-1 color-primary'
              isClick='Cancel'
              label='Cancel'
              name='Cancel'
              onClick={resetUserInputs}
              popupCloseOpenFunctionality={popupCloseOpenFunctionality}
              showhide={showHide}
              type='button'
            />
          </div>
        </div>
      ) : null}
      {showConfirmReload && (
        <DialogBox
          handleRouteNo={() => handleRoute('cancel', setShowConfirmReload)}
          handleRouteYes={handleRoute}
          message={CONCURRENCY_ERR_MSG}
          primaryText={'Ok'}
          secondaryText={'Cancel'}
        />
      )}
    </div>
  )
}

export default BasicActionButton
